import { ArrowUpRight, Download } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { shippedApps as apps } from "@/data/products";

export default function Press() {
  const { t } = useLang();
  const facts = t("press.facts");

  return (
    <div data-testid="press-page" className="bg-paper pt-28 md:pt-36">
      <div className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10 md:pb-32">
        <FadeUp><Eyebrow>{t("press.eyebrow")}</Eyebrow></FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="mt-8 max-w-3xl font-serif text-5xl font-medium leading-[1.02] tracking-tight text-ink md:text-7xl">
            {t("press.title")}
          </h1>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/60 md:text-lg">
            {t("press.body")}
          </p>
        </FadeUp>

        <div className="mt-20 grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp amount={0.3}>
              <h2 className="text-[11px] font-medium uppercase tracking-[0.25em] text-ink/40">{t("press.boilerplateLabel")}</h2>
              <p className="mt-5 max-w-2xl font-serif text-xl leading-[1.5] tracking-tight text-ink md:text-2xl">
                {t("press.boilerplate")}
              </p>
            </FadeUp>

            <FadeUp delay={0.1} amount={0.3}>
              <h2 className="mt-16 text-[11px] font-medium uppercase tracking-[0.25em] text-ink/40">{t("press.productsLabel")}</h2>
              <ul className="mt-5">
                {apps.map((p) => (
                  <li key={p.slug} className="flex flex-wrap items-baseline justify-between gap-2 border-t border-ink/10 py-4 last:border-b">
                    <span className="text-base font-semibold tracking-tight text-ink">{p.name}</span>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-ink/40">{p.compatibility}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>

          <div className="lg:col-span-5">
            <FadeUp delay={0.15} amount={0.2}>
              <div className="border border-ink/10 bg-white p-7 md:p-9">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.25em] text-ink/40">{t("press.factsLabel")}</h2>
                <dl className="mt-5">
                  {facts.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-t border-ink/5 py-3 text-sm">
                      <dt className="text-ink/50">{k}</dt>
                      <dd className="text-right text-ink">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </FadeUp>

            <FadeUp delay={0.25} amount={0.2} className="mt-6">
              <div className="border border-ink/10 bg-white p-7 md:p-9">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.25em] text-ink/40">{t("press.kitLabel")}</h2>
                <p className="mt-4 text-sm leading-relaxed text-ink/60">{t("press.kitText")}</p>
                <a
                  href="/press/logo.svg"
                  download
                  data-testid="press-logo-download"
                  className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-cobalt active:scale-[0.99]"
                >
                  <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                  {t("press.kitCta")}
                </a>
                <a
                  href="mailto:site@vlrsoftware.com?subject=Press%20enquiry"
                  data-testid="press-contact-link"
                  className="group mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-cobalt hover:text-cobalt"
                >
                  {t("press.contactCta")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </div>
  );
}
